import { useEffect, useRef } from "react";

import BusinessIcon from "@mui/icons-material/Business";
import LaunchIcon from "@mui/icons-material/Launch";
import {
  Avatar,
  Box,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
} from "@mui/material";

import { CompanyBusinessTypeChip } from "components/CompanyBusinessTypeChip";
import { buildCarrierProfileUrl } from "components/CompanyProfile/utils";
import { CarrierProfileSource } from "constants/index";

import type { CarrierSearchResult } from "../../types";

const NEW_TAB_HINT = "Opens in a new tab";

interface Props {
  id: string;
  result: CarrierSearchResult;
  isHighlighted: boolean;
  onSelect: (result: CarrierSearchResult) => void;
}

export function CarrierSearchResultRow(props: Props) {
  const { id, result, isHighlighted, onSelect } = props;
  const { company, logo } = result;

  const rowRef = useRef<HTMLAnchorElement>(null);

  // The list is taller than the dropdown, so a highlight moved by the arrow
  // keys would otherwise land outside the visible area.
  useEffect(() => {
    if (isHighlighted) {
      rowRef.current?.scrollIntoView({ block: "nearest" });
    }
  }, [isHighlighted]);

  return (
    /*
    A real anchor rather than a click handler, so the row also answers to a
    middle click and to "open link in..." from the context menu. The browser
    performs the navigation; `onSelect` only records it and closes the search.
    */
    <ListItemButton
      component="a"
      href={buildCarrierProfileUrl(
        company.id,
        CarrierProfileSource.HEADER_SEARCH,
      )}
      target="_blank"
      rel="noopener noreferrer"
      id={id}
      ref={rowRef}
      /*
      Keyboard selection is tracked by the input that owns the listbox, which
      reports this row through `aria-activedescendant`. The row only reflects
      the highlight visually, so it stays out of the tab sequence.
      */
      selected={isHighlighted}
      tabIndex={-1}
      onClick={() => onSelect(result)}
      sx={{ py: 1, color: "inherit", textDecoration: "none" }}
      role="option"
      aria-selected={isHighlighted}
    >
      <ListItemIcon sx={{ minWidth: 40 }}>
        <Avatar
          src={logo?.download_url}
          alt=""
          variant="rounded"
          sx={{ width: 28, height: 28 }}
        >
          <BusinessIcon fontSize="small" />
        </Avatar>
      </ListItemIcon>
      <ListItemText
        primary={
          <Stack direction="row" alignItems="center" spacing={1}>
            <span>{company.name}</span>
            <CompanyBusinessTypeChip businessType={company.business_type} />
          </Stack>
        }
        // The name sits beside a Chip, and neither a Stack nor a Chip is valid
        // inside the paragraph ListItemText renders by default.
        primaryTypographyProps={{
          variant: "body2",
          fontWeight: 500,
          component: "div",
        }}
      />
      {/*
      `role="option"` replaces the link semantics a screen reader would announce
      for the anchor, so the hint is labelled onto the icon instead: the option
      builds its name from its contents, and picks the hint up from there.
      */}
      <Tooltip title={NEW_TAB_HINT}>
        <Box
          component="span"
          role="img"
          aria-label={NEW_TAB_HINT}
          sx={{ display: "flex", color: "text.secondary", ml: 1 }}
        >
          <LaunchIcon fontSize="small" />
        </Box>
      </Tooltip>
    </ListItemButton>
  );
}
